import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  ClipboardDocumentCheckIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { AIGenerator } from "../../../../components/utils/AIGenerator";
import { aiGeneratorConfigs } from "../../../../config/aiGeneratorConfigs";
import { projectTaskTemplateSetsApi } from "../../../../api/tasks";
import { fetchChecklistTypes } from "../../../../api/quality/checklistType";
import { EnhancedChecklistSelector } from "../../../../components/dashboard/service/reports/enhanced-checklist-selector";
import { ProjectTaskTemplateJsonDialog } from "../../../../components/dashboard/projects/ProjectTaskTemplateJsonDialog";
import {
  parseImportedProjectTaskTemplateJson,
  resolveChecklistTypeId,
} from "../../../../components/dashboard/projects/projectTaskTemplateImportUtils";
import { RECURRENCE_OPTIONS } from "../../todos/todoConstants";

// ── Shared "Promang" form styling tokens (mirrors hours/hour_form.js) ──────────
const ACCENT = {
  solid: "#1463C7",
  grad: "linear-gradient(135deg,#1463C7,#2D8BE8)",
  glow: "rgba(20,99,199,",
};

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 700,
  color: "#334155",
  margin: "0 0 6px",
};
const subLabelStyle = { ...labelStyle, fontSize: 12, margin: "0 0 5px" };
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1.5px solid #E2E8F0",
  borderRadius: 12,
  background: "#fff",
  fontSize: 14,
  fontFamily: "inherit",
  color: "#0F172A",
  outline: "none",
};
const cardStyle = {
  border: "1.5px solid #E2E8F0",
  borderRadius: 14,
  background: "#fff",
  padding: 16,
};
const primaryBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  borderRadius: 12,
  border: "1.5px solid transparent",
  color: "#fff",
  background: ACCENT.grad,
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
  boxShadow: `0 6px 14px -5px ${ACCENT.glow}.6)`,
};
const ghostBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "9px 14px",
  borderRadius: 12,
  border: "1.5px solid #E2E8F0",
  background: "#fff",
  color: "#475569",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
};
const dangerBtnStyle = {
  ...ghostBtnStyle,
  border: "1.5px solid #FECACA",
  color: "#DC2626",
};
const checkboxLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontSize: 13,
  fontWeight: 600,
  color: "#475569",
  cursor: "pointer",
};

function emptyItem() {
  return {
    title: "",
    description: "",
    due_offset_days: 0,
    shared: false,
    completion_mode: "single",
    recurrence: "none",
    spawn_checklist: false,
    checklist_type_id: "",
  };
}

function itemToFormRow(item) {
  const action = item.reference_action || {};
  return {
    id: item.id,
    title: item.title || "",
    description: item.description || "",
    due_offset_days: item.due_offset_days ?? 0,
    shared: !!item.shared,
    completion_mode: item.completion_mode || "single",
    recurrence: item.recurrence || "none",
    spawn_checklist: action.action === "spawn_checklist",
    checklist_type_id: action.checklist_type_id || "",
  };
}

function formRowToPayload(row, index) {
  const payload = {
    title: row.title,
    description: row.description || null,
    due_offset_days: Number(row.due_offset_days) || 0,
    shared: !!row.shared,
    completion_mode: row.shared ? "any" : row.completion_mode || "single",
    recurrence: row.recurrence || "none",
    sort_order: index,
  };
  if (row.id) payload.id = row.id;
  if (row.spawn_checklist && row.checklist_type_id) {
    payload.reference_action = {
      action: "spawn_checklist",
      checklist_type_id: Number(row.checklist_type_id),
    };
  } else {
    payload.reference_action = {};
  }
  return payload;
}

export const ProjectTaskTemplatesSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [sets, setSets] = useState([]);
  const [checklistTypes, setChecklistTypes] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectTaskTemplateSetsApi.list();
      setSets(res?.data?.data || []);
    } catch (error) {
      console.error("Error loading template sets:", error);
      toast.error(t("projectTaskTemplate.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetchChecklistTypes({ per_page: 200 })
      .then((res) => {
        const rows = res?.data?.data || res?.data || [];
        setChecklistTypes(Array.isArray(rows) ? rows : []);
      })
      .catch(() => setChecklistTypes([]));
  }, []);

  const handleDelete = async (set) => {
    if (!window.confirm(t("projectTaskTemplate.delete_confirm"))) return;
    try {
      await projectTaskTemplateSetsApi.remove(set.id);
      toast.success(t("projectTaskTemplate.deleted"));
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.errors?.[0] ||
          t("projectTaskTemplate.save_failed")
      );
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-4">
      <div className="flex flex-col gap-3 border-b border-[#E2E8F0] pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">
            {t("projectTaskTemplate.title")}
          </h1>
          <p className="text-sm text-[#64748B]">
            {t("projectTaskTemplate.subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={() =>
            setEditing({
              title: "",
              description: "",
              category: "",
              active: true,
              items: [emptyItem()],
            })
          }
          style={primaryBtnStyle}
        >
          <PlusIcon className="h-4 w-4" />
          {t("projectTaskTemplate.new_set")}
        </button>
      </div>

      {loading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <ResourceLoading />
        </div>
      ) : sets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#CBD5E1] py-10 text-center text-sm text-[#64748B]">
          {t("projectTaskTemplate.empty")}
        </div>
      ) : (
        <ul className="space-y-3">
          {sets.map((set) => (
            <li
              key={set.id}
              className="flex items-center justify-between gap-3"
              style={cardStyle}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-bold text-[#0F172A]">
                    {set.title}
                  </p>
                  {!set.active && (
                    <span className="rounded-full bg-[#F1F5F9] px-2 py-0.5 text-xs font-semibold text-[#64748B]">
                      {t("projectTaskTemplate.set_active")}: ✕
                    </span>
                  )}
                </div>
                <p className="truncate text-xs text-[#64748B]">
                  {[
                    set.category,
                    t("project.tasks.template_item_count", {
                      count: set.item_count ?? 0,
                    }),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setEditing({
                      id: set.id,
                      title: set.title,
                      description: set.description || "",
                      category: set.category || "",
                      active: set.active !== false,
                      items: (set.items || []).map(itemToFormRow),
                    })
                  }
                  style={ghostBtnStyle}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  {t("projectTaskTemplate.edit")}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(set)}
                  style={dangerBtnStyle}
                >
                  <TrashIcon className="h-4 w-4" />
                  {t("projectTaskTemplate.delete")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <EditSetModal
          initial={editing}
          checklistTypes={checklistTypes}
          t={t}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
};

const EditSetModal = ({ initial, checklistTypes, t, onClose, onSaved }) => {
  const [title, setTitle] = useState(initial.title || "");
  const [description, setDescription] = useState(initial.description || "");
  const [category, setCategory] = useState(initial.category || "");
  const [active, setActive] = useState(initial.active !== false);
  const [items, setItems] = useState(
    initial.items && initial.items.length ? initial.items : [emptyItem()]
  );
  const [saving, setSaving] = useState(false);
  // Index of the item whose checklist-type picker modal is open (null = closed).
  const [pickerIndex, setPickerIndex] = useState(null);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);

  const applyParsedTemplate = (parsed, mode = "replace") => {
    const mapped = (parsed.items || []).map((row) => ({
      ...row,
      checklist_type_id: resolveChecklistTypeId(row, checklistTypes),
    }));
    if (mode === "append") {
      setItems((prev) => [...prev, ...mapped]);
    } else {
      setItems(mapped.length ? mapped : [emptyItem()]);
    }
    if (parsed.title) setTitle(parsed.title);
    if (parsed.description != null && parsed.description !== "") {
      setDescription(parsed.description);
    }
    if (parsed.category != null && parsed.category !== "") {
      setCategory(parsed.category);
    }
    if (parsed.active != null) setActive(Boolean(parsed.active));
  };

  const updateItem = (index, patch) => {
    setItems((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const checklistTypeLabel = (row) => {
    const selected = checklistTypes.find(
      (ct) => String(ct.id) === String(row.checklist_type_id)
    );
    if (selected) return selected.displayTitle || selected.title || `#${selected.id}`;
    return t("projectTaskTemplate.select_checklist_type");
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error(t("project.tasks.title_required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        category: category.trim() || null,
        active,
        items: items
          .filter((row) => row.title.trim())
          .map((row, index) => formRowToPayload(row, index)),
      };
      if (initial.id) {
        await projectTaskTemplateSetsApi.update(initial.id, payload);
      } else {
        await projectTaskTemplateSetsApi.create(payload);
      }
      toast.success(t("projectTaskTemplate.saved"));
      await onSaved();
    } catch (error) {
      toast.error(
        error?.response?.data?.errors?.[0] ||
          t("projectTaskTemplate.save_failed")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F172A]/40 p-4">
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl"
        style={{ borderRadius: 18 }}
      >
        <div className="flex items-center justify-between border-b border-[#E2E8F0] px-5 py-4">
          <h3 className="text-base font-bold text-[#0F172A]">
            {initial.id
              ? t("projectTaskTemplate.edit_set")
              : t("projectTaskTemplate.new_set")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#475569]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto bg-[#F8FAFC] p-5">
          {/* Set details */}
          <section style={cardStyle} className="space-y-4">
            <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-2">
              <div>
                <label style={labelStyle}>
                  {t("projectTaskTemplate.set_title")}
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  {t("projectTaskTemplate.set_category")}
                </label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle}>
                {t("projectTaskTemplate.set_description")}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </div>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-[#CBD5E1]"
              />
              {t("projectTaskTemplate.set_active")}
            </label>
          </section>

          {/* Items */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-bold text-[#334155]">
                {t("projectTaskTemplate.items")}
              </h4>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setJsonDialogOpen(true)}
                  style={ghostBtnStyle}
                >
                  <CodeBracketIcon className="h-4 w-4" />
                  {t("projectTaskTemplate.json.open_dialog")}
                </button>
                <AIGenerator
                  module="project_task_template"
                  context={{
                    set_title: title || "",
                    set_description: description || "",
                    set_category: category || "",
                  }}
                  fieldsToGenerate={["set_json"]}
                  onGenerate={(generatedFields, meta) => {
                    const raw = generatedFields?.set_json;
                    if (raw == null || typeof raw !== "string" || !String(raw).trim()) {
                      toast.error(t("projectTaskTemplate.json.ai_parse_error"));
                      return;
                    }
                    try {
                      const parsed = parseImportedProjectTaskTemplateJson(raw);
                      const applyMode = meta?.generationOptions?.apply_mode || "replace";
                      applyParsedTemplate(parsed, applyMode);
                      toast.success(
                        t("projectTaskTemplate.json.ai_apply_success", {
                          count: parsed.items.length,
                        })
                      );
                    } catch (err) {
                      const code = err?.code || "unknown";
                      const key = `projectTaskTemplate.json.error_${code}`;
                      const msg = t(key);
                      toast.error(
                        msg !== key ? msg : t("projectTaskTemplate.json.ai_parse_error")
                      );
                    }
                  }}
                  config={aiGeneratorConfigs.project_task_template}
                  buttonLabel={t("projectTaskTemplate.json.generate_with_ai")}
                  className="!rounded-full !border-slate-300 !px-4 !py-2 !text-sm !font-semibold"
                />
                <button
                  type="button"
                  onClick={() => setItems((prev) => [...prev, emptyItem()])}
                  style={ghostBtnStyle}
                >
                  <PlusIcon className="h-4 w-4" />
                  {t("projectTaskTemplate.add_item")}
                </button>
              </div>
            </div>

            <ul className="space-y-3">
              {items.map((row, index) => (
                <li key={index} style={cardStyle} className="space-y-3">
                  <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-4">
                    <div className="sm:col-span-2">
                      <label style={subLabelStyle}>
                        {t("projectTaskTemplate.item_title")}
                      </label>
                      <input
                        type="text"
                        value={row.title}
                        onChange={(e) =>
                          updateItem(index, { title: e.target.value })
                        }
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={subLabelStyle}>
                        {t("projectTaskTemplate.item_due_offset")}
                      </label>
                      <input
                        type="number"
                        value={row.due_offset_days}
                        onChange={(e) =>
                          updateItem(index, {
                            due_offset_days: e.target.value,
                          })
                        }
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={subLabelStyle}>
                        {t("projectTaskTemplate.item_recurrence")}
                      </label>
                      <select
                        value={row.recurrence}
                        onChange={(e) =>
                          updateItem(index, { recurrence: e.target.value })
                        }
                        style={inputStyle}
                      >
                        {RECURRENCE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(opt.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4">
                    <label style={checkboxLabelStyle}>
                      <input
                        type="checkbox"
                        checked={row.shared}
                        onChange={(e) =>
                          updateItem(index, { shared: e.target.checked })
                        }
                        className="h-4 w-4 rounded border-[#CBD5E1]"
                      />
                      {t("projectTaskTemplate.item_shared")}
                    </label>
                    <label style={checkboxLabelStyle}>
                      <input
                        type="checkbox"
                        checked={row.spawn_checklist}
                        onChange={(e) =>
                          updateItem(index, {
                            spawn_checklist: e.target.checked,
                          })
                        }
                        className="h-4 w-4 rounded border-[#CBD5E1]"
                      />
                      {t("projectTaskTemplate.spawn_checklist")}
                    </label>
                    {row.spawn_checklist && (
                      <button
                        type="button"
                        onClick={() => setPickerIndex(index)}
                        style={ghostBtnStyle}
                      >
                        <ClipboardDocumentCheckIcon className="h-4 w-4" />
                        {checklistTypeLabel(row)}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      style={{ ...dangerBtnStyle, marginLeft: "auto" }}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="flex justify-end gap-2 border-t border-[#E2E8F0] px-5 py-4">
          <button type="button" onClick={onClose} style={ghostBtnStyle}>
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{ ...primaryBtnStyle, opacity: saving ? 0.5 : 1 }}
          >
            {t("projectTaskTemplate.save")}
          </button>
        </div>
      </div>

      {/* Checklist type picker — reuses the shared EnhancedChecklistSelector adapted to
          the current system. Only the "type" mode is offered (no empty/title); we capture
          just the selected checklist_type_id into the item's reference_action. */}
      <EnhancedChecklistSelector
        open={pickerIndex !== null}
        onClose={() => setPickerIndex(null)}
        showEmptyOption={false}
        showTitleField={false}
        autoTitleFromType={false}
        onSelect={(config) => {
          if (pickerIndex !== null && config?.checklist_type_id) {
            updateItem(pickerIndex, {
              checklist_type_id: config.checklist_type_id,
            });
          }
          setPickerIndex(null);
        }}
      />

      <ProjectTaskTemplateJsonDialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        title={title}
        description={description}
        category={category}
        active={active}
        items={items}
        onApply={({ mode, ...parsed }) => applyParsedTemplate(parsed, mode || "replace")}
      />
    </div>
  );
};

export default ProjectTaskTemplatesSettings;
